import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  SpeakerHigh,
  ArrowLeft,
  ArrowRight,
  Star,
  Heart,
  Sparkle,
  BookOpenText,
  Ear,
  PuzzlePiece,
  PencilLine,
  User,
  Gear,
  Check,
  X,
  ArrowClockwise,
  Trophy,
  DownloadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { loadActivityProgress, saveActivityProgress } from "@/lib/activityProgress";
import { useLumi } from "@/components/LumiCharacter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Always use the backend voice (ElevenLabs, with OpenAI fallback) so Lumi sounds the same in every browser
let _lumiAudio = null;
const speak = async (text) => {
  if (!text) return;
  try {
    if (_lumiAudio) _lumiAudio.pause();
    const res = await axios.post(`${API}/kids/lumi-speak`, { text });
    if (!res.data?.audio_base64) return;
    _lumiAudio = new Audio(`data:audio/mp3;base64,${res.data.audio_base64}`);
    _lumiAudio.play().catch(() => {});
  } catch {
    /* silent */
  }
};

const encouragements = ["Great job!", "You've got this!", "Nice thinking!", "Keep going!", "Superstar!"];
const gentleTryAgain = ["Almost! Try again.", "So close! Give it another go.", "Not quite — you can do it!"];

// ─────────────────────────────────────────────────────────────
// Main page — parent-owned profiles + 3 games + progress
// ─────────────────────────────────────────────────────────────
const KidsVocab = () => {
  const lumi = useLumi();
  const [view, setView] = useState("home"); // home | setup | play | progress
  const [parentEmail, setParentEmail] = useState(() => localStorage.getItem("vocab_parent_email") || "");
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vocab_active_profile")) || null; } catch { return null; }
  });
  const [activeGame, setActiveGame] = useState(null); // 'hear' | 'tiles' | 'trace'

  const loadProfiles = useCallback(async (email) => {
    if (!email) return;
    try {
      const res = await axios.get(`${API}/vocab/profiles`, { params: { parent_email: email } });
      setProfiles(res.data || []);
    } catch { /* silent */ }
  }, []);

  // Returning-parent lookup — recognizes an existing parent by email alone,
  // independent of localStorage (e.g. cleared cache / new device).
  const [lookupStatus, setLookupStatus] = useState(""); // "" | "not_found" | "error"
  const lookupExisting = useCallback(async (email) => {
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed) return;
    setLookupStatus("");
    try {
      const res = await axios.get(`${API}/vocab/profiles`, { params: { parent_email: trimmed } });
      const found = res.data || [];
      if (found.length > 0) {
        localStorage.setItem("vocab_parent_email", trimmed);
        setParentEmail(trimmed);
        setProfiles(found);
      } else {
        setLookupStatus("not_found");
      }
    } catch {
      setLookupStatus("error");
    }
  }, []);

  useEffect(() => {
    if (parentEmail) loadProfiles(parentEmail);
  }, [parentEmail, loadProfiles]);

  useEffect(() => {
    // Welcome
    lumi.wave("Welcome to Word Play! 🌟 Pick a friend to begin.", 3500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectProfile = (p) => {
    setActiveProfile(p);
    localStorage.setItem("vocab_active_profile", JSON.stringify(p));
    setView("play");
  };

  const clearActive = () => {
    setActiveProfile(null);
    localStorage.removeItem("vocab_active_profile");
    setView("home");
    setActiveGame(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-yellow-50 to-pink-50 pb-36 sm:pb-8">
      <PWAInstallPrompt />

      {/* Header */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b-4 border-foreground">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <a href="/kids-corner" className="flex items-center gap-2" data-testid="vocab-back-btn">
            <ArrowLeft size={20} weight="bold" />
            <span className="font-fredoka font-bold text-lg">Word Play</span>
          </a>
          {activeProfile && (
            <div className="flex items-center gap-2">
              <span className="hidden sm:flex items-center gap-2 bg-yellow-100 border-2 border-foreground rounded-full px-3 py-1">
                <User size={16} weight="fill" />
                <span className="font-fredoka font-bold text-sm">{activeProfile.child_nickname}</span>
              </span>
              <button
                onClick={() => setView("progress")}
                className="p-2 rounded-full bg-white border-2 border-foreground shadow-brutal-sm hover:bg-muted"
                aria-label="View progress"
                data-testid="vocab-progress-btn"
              >
                <Trophy size={18} weight="fill" className="text-yellow-500" />
              </button>
              <button
                onClick={clearActive}
                className="p-2 rounded-full bg-white border-2 border-foreground shadow-brutal-sm hover:bg-muted"
                aria-label="Switch profile"
              >
                <Gear size={18} weight="fill" />
              </button>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
        {view === "home" && (
          <HomeView
            parentEmail={parentEmail}
            setParentEmail={setParentEmail}
            profiles={profiles}
            onSelect={selectProfile}
            onNew={() => setView("setup")}
            reloadProfiles={loadProfiles}
            onLookup={lookupExisting}
            lookupStatus={lookupStatus}
          />
        )}
        {view === "setup" && (
          <SetupView
            parentEmail={parentEmail}
            setParentEmail={setParentEmail}
            onCreated={(profile) => {
              setProfiles((p) => [...p, profile]);
              selectProfile(profile);
            }}
            onCancel={() => setView("home")}
          />
        )}
        {view === "play" && activeProfile && !activeGame && (
          <PlayHub profile={activeProfile} onStart={setActiveGame} />
        )}
        {view === "play" && activeProfile && activeGame && (
          <GameRunner
            profile={activeProfile}
            game={activeGame}
            onExit={() => setActiveGame(null)}
          />
        )}
        {view === "progress" && activeProfile && (
          <ProgressView profile={activeProfile} onBack={() => setView("play")} />
        )}
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PWA install prompt
// ─────────────────────────────────────────────────────────────
const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  if (!deferred) return null;
  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 bg-white border-2 border-foreground rounded-2xl shadow-brutal p-4 flex items-center gap-3">
      <DownloadSimple size={28} weight="fill" className="text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-fredoka font-bold text-foreground text-sm">Install Word Play</p>
        <p className="font-nunito text-xs text-slate-600">Add to your home screen for a full-app feel.</p>
      </div>
      <Button
        size="sm"
        onClick={async () => {
          deferred.prompt();
          await deferred.userChoice;
          setDeferred(null);
        }}
        className="bg-primary text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-full"
      >
        Install
      </Button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Home — pick a child profile (or create one)
// ─────────────────────────────────────────────────────────────
const HomeView = ({ parentEmail, profiles, onSelect, onNew, onLookup, lookupStatus }) => {
  const [showLookup, setShowLookup] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");

  const submitLookup = (e) => {
    e.preventDefault();
    onLookup(lookupEmail);
  };

  return (
    <div>
      <div className="text-center mb-8">
        <span className="font-caveat text-2xl text-accent-purple -rotate-2 inline-block">
          Hi grown-ups!
        </span>
        <h1 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-1">
          Who's playing today?
        </h1>
        <p className="font-nunito text-slate-600 mt-2">
          Private profiles — one per child. Scores and stars stay just between you and your little learner.
        </p>
      </div>

      {!parentEmail ? (
        <div className="max-w-md mx-auto bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 text-center">
          <p className="font-nunito text-foreground mb-4">
            To keep progress safe, we ask for a <strong>grown-up's email</strong> first — kids don't need one.
          </p>
          <Button
            onClick={onNew}
            className="bg-primary text-white font-fredoka font-bold text-lg border-2 border-foreground shadow-brutal active-brutal rounded-full px-8 py-6"
            data-testid="vocab-start-setup-btn"
          >
            <Sparkle size={20} weight="fill" className="mr-2" />
            Get Started
          </Button>

          <div className="mt-5 pt-5 border-t-2 border-dashed border-foreground/20">
            {!showLookup ? (
              <button
                type="button"
                onClick={() => setShowLookup(true)}
                className="font-nunito text-sm text-foreground/70 underline hover:text-foreground"
                data-testid="vocab-show-lookup-btn"
              >
                Already have a profile? Find it by email
              </button>
            ) : (
              <form onSubmit={submitLookup} className="flex flex-col gap-2 text-left">
                <label className="font-nunito font-bold text-sm">Your email</label>
                <Input
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="font-nunito border-2 border-foreground rounded-xl py-5"
                  data-testid="vocab-lookup-email"
                />
                <Button
                  type="submit"
                  variant="outline"
                  className="font-nunito font-bold border-2 border-foreground rounded-xl"
                  data-testid="vocab-lookup-submit-btn"
                >
                  Find my profile
                </Button>
                {lookupStatus === "not_found" && (
                  <p className="font-nunito text-sm text-slate-600">
                    We couldn't find a profile for that email. You can create a new one above.
                  </p>
                )}
                {lookupStatus === "error" && (
                  <p className="font-nunito text-sm text-red-600">
                    Something went wrong — please try again.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {profiles.map((p) => (
            <motion.button
              key={p.id}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onSelect(p)}
              className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 flex items-center gap-4 text-left"
              data-testid={`vocab-profile-${p.id}`}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-sky-400 to-purple-400 border-2 border-foreground rounded-full flex items-center justify-center shrink-0">
                <span className="font-fredoka font-bold text-white text-xl">
                  {p.child_nickname.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-fredoka font-bold text-xl text-foreground truncate">{p.child_nickname}</p>
                <p className="font-nunito text-sm text-slate-600 capitalize">{p.difficulty} · {(p.topics || []).length === 0 ? "All topics" : p.topics.join(", ")}</p>
              </div>
              <ArrowRight size={24} weight="bold" className="text-primary shrink-0" />
            </motion.button>
          ))}
          <motion.button
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            onClick={onNew}
            className="bg-yellow-100 border-4 border-dashed border-foreground rounded-3xl p-6 flex flex-col items-center justify-center gap-2 min-h-[120px]"
            data-testid="vocab-add-profile-btn"
          >
            <span className="w-12 h-12 rounded-full bg-white border-2 border-foreground flex items-center justify-center text-2xl">+</span>
            <p className="font-fredoka font-bold text-foreground">Add a child</p>
          </motion.button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Setup view — parent creates a profile
// ─────────────────────────────────────────────────────────────
const SetupView = ({ parentEmail, setParentEmail, onCreated, onCancel }) => {
  const [email, setEmail] = useState(parentEmail);
  const [nickname, setNickname] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [meta, setMeta] = useState({ topics: [], difficulties: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vocab/topics`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const toggleTopic = (t) => {
    setSelectedTopics((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !nickname.trim()) {
      toast.error("Please enter your email and your child's nickname");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/vocab/profile`, {
        parent_email: email.trim().toLowerCase(),
        child_nickname: nickname,
        difficulty,
        topics: selectedTopics,
      });
      localStorage.setItem("vocab_parent_email", email.trim().toLowerCase());
      setParentEmail(email.trim().toLowerCase());
      toast.success(`${res.data.child_nickname} is all set! ✨`);
      onCreated(res.data);
    } catch (err) {
      toast.error("Couldn't create profile — please try again");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-xl mx-auto">
      <button
        onClick={onCancel}
        className="font-nunito font-bold text-sm text-foreground/70 hover:text-foreground mb-4"
      >
        ← Back
      </button>
      <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 sm:p-8">
        <h2 className="font-fredoka font-bold text-2xl text-foreground mb-1">Set up a child profile</h2>
        <p className="font-nunito text-sm text-slate-600 mb-5">
          One-time setup. You can add more children later. Your child never enters an email.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-nunito font-bold text-sm mb-1 block">Your email (grown-up only)</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="font-nunito border-2 border-foreground rounded-xl py-5"
              data-testid="vocab-setup-email"
            />
          </div>
          <div>
            <label className="font-nunito font-bold text-sm mb-1 block">Your child's nickname</label>
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Star, Buddy, Mia…"
              required
              maxLength={20}
              className="font-nunito border-2 border-foreground rounded-xl py-5"
              data-testid="vocab-setup-nickname"
            />
            <p className="font-nunito text-xs text-slate-500 mt-1">
              Any name your child likes — no need for a real name.
            </p>
          </div>
          <div>
            <label className="font-nunito font-bold text-sm mb-2 block">Level</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: "easy", label: "Easy (K-1)" },
                { id: "medium", label: "Medium (2-3)" },
                { id: "hard", label: "Hard (4-5)" },
              ].map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDifficulty(d.id)}
                  className={`px-4 py-2 rounded-full border-2 border-foreground font-nunito font-bold text-sm ${
                    difficulty === d.id ? "bg-primary text-white" : "bg-white hover:bg-muted"
                  }`}
                  data-testid={`vocab-diff-${d.id}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-nunito font-bold text-sm mb-2 block">Word topics (pick any — or leave all off for a mix)</label>
            <div className="flex gap-2 flex-wrap">
              {meta.topics.map((t) => {
                const active = selectedTopics.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTopic(t)}
                    className={`px-3 py-1.5 rounded-full border-2 border-foreground font-nunito font-bold text-sm ${
                      active ? "bg-accent-purple text-white" : "bg-white hover:bg-muted"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-primary hover:bg-primary-hover text-white font-fredoka font-bold text-lg border-2 border-foreground shadow-brutal active-brutal rounded-xl py-6"
            data-testid="vocab-setup-submit"
          >
            {saving ? "Saving…" : "Start Playing"}
            <ArrowRight size={20} weight="bold" className="ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Play Hub — pick which of 3 games
// ─────────────────────────────────────────────────────────────
const PlayHub = ({ profile, onStart }) => (
  <div>
    <div className="text-center mb-6">
      <h1 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground">
        Pick a game, {profile.child_nickname}!
      </h1>
      <p className="font-nunito text-slate-600 mt-1">Take your time — no timers here.</p>
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      {[
        { id: "hear", title: "Hear & Choose", desc: "Listen to a word, tap the picture", icon: Ear, gradient: "from-sky-200 to-sky-300", btn: "bg-sky-500 hover:bg-sky-600" },
        { id: "tiles", title: "Sentence Tiles", desc: "Drag words to build a sentence", icon: PuzzlePiece, gradient: "from-emerald-200 to-teal-300", btn: "bg-emerald-500 hover:bg-emerald-600" },
        { id: "trace", title: "Trace & Say", desc: "Trace the word with your finger", icon: PencilLine, gradient: "from-pink-200 to-purple-300", btn: "bg-purple-500 hover:bg-purple-600" },
      ].map((g) => (
        <motion.button
          key={g.id}
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onStart(g.id)}
          className={`text-left bg-gradient-to-br ${g.gradient} border-4 border-foreground rounded-3xl shadow-brutal p-5 flex flex-col justify-between min-h-[220px]`}
          data-testid={`vocab-game-${g.id}`}
        >
          <div>
            <div className="w-14 h-14 bg-white rounded-2xl border-2 border-foreground flex items-center justify-center mb-3">
              <g.icon size={30} weight="duotone" className="text-foreground" />
            </div>
            <h3 className="font-fredoka font-bold text-xl text-foreground">{g.title}</h3>
            <p className="font-nunito text-sm text-foreground/80 mt-1">{g.desc}</p>
          </div>
          <div className={`${g.btn} text-white font-fredoka font-bold text-sm border-2 border-foreground shadow-brutal-sm rounded-xl py-3 px-4 flex items-center justify-center mt-4`}>
            Play
            <ArrowRight size={18} weight="bold" className="ml-2" />
          </div>
        </motion.button>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Game runner — fetches word set, cycles through rounds
// ─────────────────────────────────────────────────────────────
const GameRunner = ({ profile, game, onExit }) => {
  // Progress per child (profile) + game: { words, idx, stars, completed } — resumes an unfinished set and keeps
  // finished words out of new sets until every word for this topic/difficulty has been played.
  const saved = loadActivityProgress("kids_wp_progress", profile.id, game);
  const resuming = saved && Array.isArray(saved.words) && saved.words.length > 0 && saved.idx < saved.words.length;
  const [words, setWords] = useState(resuming ? saved.words : []);
  const [idx, setIdx] = useState(resuming ? saved.idx : 0);
  const [stars, setStars] = useState(resuming ? saved.stars || 0 : 0);
  const [done, setDone] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const completedRef = useRef(saved?.completed || []);
  const persist = (w, i, st) => saveActivityProgress("kids_wp_progress", profile.id, game, { words: w, idx: i, stars: st, completed: completedRef.current });

  const load = async () => {
    const topic = (profile.topics || []).length === 1 ? profile.topics[0] : "All";
    const params = { topic, difficulty: profile.difficulty, limit: 6, profile_id: profile.id, exclude: completedRef.current.join(",") };
    let res = await axios.get(`${API}/vocab/words`, { params });
    if (res.data.length === 0 && completedRef.current.length) {
      // The whole set is finished — start a fresh pass through all the words.
      completedRef.current = [];
      res = await axios.get(`${API}/vocab/words`, { params: { ...params, exclude: "" } });
    }
    if (res.data.length === 0) { setExhausted(true); return; }
    setWords(res.data);
    setIdx(0);
    setStars(0);
    persist(res.data, 0, 0);
  };

  useEffect(() => {
    if (resuming) { toast("Welcome back! Picking up where you left off.", { icon: "✨" }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, game]);

  const recordAttempt = async (word, correct) => {
    try {
      await axios.post(`${API}/vocab/attempt`, {
        profile_id: profile.id,
        word,
        game,
        correct,
      });
    } catch { /* silent */ }
  };

  const next = (correct) => {
    const st = correct ? stars + 1 : stars;
    setStars(st);
    const w = words[idx]?.word;
    if (w && !completedRef.current.includes(w)) completedRef.current = [...completedRef.current, w];
    if (idx + 1 >= words.length) {
      setDone(true);
      persist(words, words.length, st); // set finished: nothing to resume; finished words stay excluded
    } else {
      setIdx(idx + 1);
      persist(words, idx + 1, st);
    }
  };

  if (exhausted) {
    return <p className="text-center font-nunito py-16" data-testid="vocab-no-words">No words found for this topic yet.</p>;
  }
  if (words.length === 0) {
    return <p className="text-center font-nunito py-16">Loading your words…</p>;
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto text-center bg-white border-4 border-foreground rounded-3xl shadow-brutal p-8 mt-6">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
          <Trophy size={72} weight="fill" className="text-yellow-500 mx-auto mb-3" />
        </motion.div>
        <h2 className="font-fredoka font-bold text-3xl text-foreground mb-2">
          You did it, {profile.child_nickname}!
        </h2>
        <p className="font-nunito text-slate-700 mb-4">
          You earned <strong>{stars}</strong> star{stars === 1 ? "" : "s"} out of {words.length}.
        </p>
        <div className="flex gap-1 justify-center mb-6" data-testid="vocab-final-stars">
          {Array.from({ length: words.length }).map((_, i) => (
            <Star key={i} size={28} weight={i < stars ? "fill" : "regular"} className={i < stars ? "text-yellow-500" : "text-slate-300"} />
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onExit}
            variant="outline"
            className="flex-1 font-nunito border-2 border-foreground rounded-xl"
            data-testid="vocab-back-hub-btn"
          >
            Back to games
          </Button>
          <Button
            onClick={() => { setDone(false); setWords([]); load(); }}
            data-testid="vocab-play-again-btn"
            className="flex-1 bg-primary text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-xl"
          >
            <ArrowClockwise size={18} weight="bold" className="mr-2" />
            Play again
          </Button>
        </div>
      </div>
    );
  }

  const current = words[idx];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onExit} className="font-nunito font-bold text-sm text-foreground/70" data-testid="vocab-exit-game-btn">
          ← Games
        </button>
        <div className="flex items-center gap-1">
          {Array.from({ length: words.length }).map((_, i) => (
            <span
              key={i}
              className={`w-2.5 h-2.5 rounded-full border border-foreground ${
                i < idx ? "bg-emerald-500" : i === idx ? "bg-primary" : "bg-white"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1 bg-yellow-100 border-2 border-foreground rounded-full px-3 py-0.5">
          <Star size={16} weight="fill" className="text-yellow-500" />
          <span className="font-fredoka font-bold text-sm">{stars}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${game}-${idx}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
        >
          {game === "hear" && <HearAndChoose word={current} onDone={(ok) => { recordAttempt(current.word, ok); next(ok); }} />}
          {game === "tiles" && <SentenceTiles word={current} onDone={(ok) => { recordAttempt(current.word, ok); next(ok); }} />}
          {game === "trace" && <TraceAndSay word={current} onDone={(ok) => { recordAttempt(current.word, ok); next(ok); }} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Feedback helper — encouraging, explains correct answer
// ─────────────────────────────────────────────────────────────
const Feedback = ({ correct, word, definition, onRetry, onNext }) => (
  <div
    className={`mt-4 border-2 border-foreground rounded-2xl p-4 sm:p-5 ${
      correct ? "bg-emerald-50" : "bg-amber-50"
    }`}
    data-testid="vocab-feedback"
  >
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 border-foreground ${correct ? "bg-emerald-500" : "bg-amber-400"}`}>
        {correct ? <Check size={20} weight="bold" className="text-white" /> : <Heart size={20} weight="fill" className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-fredoka font-bold text-foreground">
          {correct ? encouragements[Math.floor(Math.random() * encouragements.length)] : gentleTryAgain[Math.floor(Math.random() * gentleTryAgain.length)]}
        </p>
        <p className="font-nunito text-sm text-slate-700 mt-1">
          <strong>{word}</strong> means <em>{definition}</em>.
        </p>
      </div>
    </div>
    <div className="flex gap-2 mt-3">
      {!correct && (
        <Button
          onClick={onRetry}
          variant="outline"
          className="flex-1 font-nunito border-2 border-foreground rounded-xl"
          data-testid="vocab-retry-btn"
        >
          <ArrowClockwise size={16} weight="bold" className="mr-1.5" />
          Try again
        </Button>
      )}
      <Button
        onClick={onNext}
        className="flex-1 bg-primary text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-xl"
        data-testid="vocab-next-btn"
      >
        {correct ? "Next" : "Skip"}
        <ArrowRight size={18} weight="bold" className="ml-2" />
      </Button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Game 1: Hear & Choose — audio word, tap correct emoji
// ─────────────────────────────────────────────────────────────
const HearAndChoose = ({ word, onDone }) => {
  const [phase, setPhase] = useState("play"); // play | feedback
  const [wasCorrect, setWasCorrect] = useState(false);
  const [choices] = useState(() => shuffle([word.emoji, ...word.distractors]));
  const [selected, setSelected] = useState(null);

  // Auto-speak the word on mount
  useEffect(() => {
    const t = setTimeout(() => speak(word.word), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.word]);

  const pick = (emoji) => {
    if (phase === "feedback") return;
    setSelected(emoji);
    const correct = emoji === word.emoji;
    setWasCorrect(correct);
    speak(correct ? "Yes! " + word.word : "Hmm, that's the " + (emoji === word.distractors[0] ? "" : "") + "word for something else.");
    setPhase("feedback");
  };

  return (
    <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-5 sm:p-8">
      <div className="text-center mb-5">
        <p className="font-nunito text-sm text-slate-600 uppercase tracking-widest">Tap the picture that shows…</p>
        <button
          onClick={() => speak(word.word)}
          className="mt-3 inline-flex items-center gap-2 bg-primary text-white font-fredoka font-bold text-2xl sm:text-3xl border-2 border-foreground shadow-brutal active-brutal rounded-2xl px-6 py-3"
          data-testid="vocab-hear-play-btn"
        >
          <SpeakerHigh size={28} weight="fill" />
          Say "{word.word}"
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {choices.map((emoji) => {
          const isPicked = selected === emoji;
          const isCorrect = emoji === word.emoji;
          const showState = phase === "feedback" && isPicked;
          return (
            <button
              key={emoji}
              onClick={() => pick(emoji)}
              disabled={phase === "feedback"}
              className={`aspect-square rounded-2xl border-4 border-foreground shadow-brutal active-brutal flex items-center justify-center text-6xl sm:text-7xl transition-transform ${
                showState
                  ? isCorrect
                    ? "bg-emerald-200"
                    : "bg-red-100"
                  : "bg-yellow-50 hover:bg-yellow-100"
              }`}
              data-testid={`vocab-hear-choice-${emoji}`}
              aria-label={`Choose ${emoji}`}
            >
              {emoji}
            </button>
          );
        })}
      </div>

      {phase === "feedback" && (
        <Feedback
          correct={wasCorrect}
          word={word.word}
          definition={word.definition}
          onRetry={() => { setPhase("play"); setSelected(null); }}
          onNext={() => onDone(wasCorrect)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Game 2: Sentence Tiles — arrange words to build a sentence
// ─────────────────────────────────────────────────────────────
const SentenceTiles = ({ word, onDone }) => {
  const template = word.sentence_template;
  const [pool, setPool] = useState(() => shuffle(template.tokens));
  const [built, setBuilt] = useState([]);
  const [phase, setPhase] = useState("play"); // play | feedback
  const [wasCorrect, setWasCorrect] = useState(false);

  const check = () => {
    const correct = built.join(" ") === template.sentence;
    setWasCorrect(correct);
    if (correct) {
      speak(template.sentence);
    }
    setPhase("feedback");
  };

  const takeTile = (i) => {
    if (phase === "feedback") return;
    setBuilt((b) => [...b, pool[i]]);
    setPool((p) => p.filter((_, idx) => idx !== i));
  };

  const removeBuilt = (i) => {
    if (phase === "feedback") return;
    setPool((p) => [...p, built[i]]);
    setBuilt((b) => b.filter((_, idx) => idx !== i));
  };

  const reset = () => {
    setPool(shuffle(template.tokens));
    setBuilt([]);
    setPhase("play");
  };

  return (
    <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-5 sm:p-8">
      <p className="text-center font-nunito text-sm text-slate-600 uppercase tracking-widest mb-3">
        Build a sentence with the word
      </p>
      <p className="text-center">
        <span className="inline-block bg-yellow-100 border-2 border-foreground rounded-full px-4 py-1 font-fredoka font-bold text-2xl text-foreground">
          {word.word}
        </span>
      </p>

      {/* Sentence "line" */}
      <div className="min-h-[80px] mt-5 border-b-4 border-dashed border-foreground/30 pb-2 flex flex-wrap gap-2 justify-center">
        {built.length === 0 ? (
          <p className="font-nunito text-slate-400 self-end">Tap tiles below to build here…</p>
        ) : (
          built.map((tok, i) => (
            <button
              key={i}
              onClick={() => removeBuilt(i)}
              className="bg-primary text-white font-fredoka font-bold text-lg sm:text-xl border-2 border-foreground rounded-xl px-3 py-2 shadow-brutal-sm"
              data-testid={`vocab-built-${i}`}
            >
              {tok}
            </button>
          ))
        )}
      </div>

      {/* Tile pool */}
      <div className="mt-5 flex flex-wrap gap-2 justify-center min-h-[60px]">
        {pool.map((tok, i) => (
          <button
            key={`${tok}-${i}`}
            onClick={() => takeTile(i)}
            className="bg-yellow-100 border-2 border-foreground rounded-xl px-3 py-2 font-fredoka font-bold text-lg sm:text-xl shadow-brutal-sm hover:bg-yellow-200"
            data-testid={`vocab-tile-${tok}`}
          >
            {tok}
          </button>
        ))}
      </div>

      {phase === "play" && (
        <div className="flex gap-2 mt-5">
          <Button
            onClick={reset}
            variant="outline"
            className="font-nunito border-2 border-foreground rounded-xl"
          >
            <ArrowClockwise size={16} weight="bold" className="mr-1.5" />
            Reset
          </Button>
          <Button
            onClick={check}
            disabled={pool.length > 0}
            className="flex-1 bg-primary text-white font-fredoka font-bold border-2 border-foreground shadow-brutal-sm rounded-xl py-5 disabled:opacity-60"
            data-testid="vocab-tiles-check-btn"
          >
            Check my sentence
            <Check size={20} weight="bold" className="ml-2" />
          </Button>
        </div>
      )}

      {phase === "feedback" && (
        <Feedback
          correct={wasCorrect}
          word={word.word}
          definition={word.definition}
          onRetry={reset}
          onNext={() => onDone(wasCorrect)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Game 3: Trace & Say — trace with finger, then confirm you can say a sentence
// ─────────────────────────────────────────────────────────────
const TraceAndSay = ({ word, onDone }) => {
  const canvasRef = useRef(null);
  const [tracing, setTracing] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const [phase, setPhase] = useState("trace"); // trace | say | feedback
  const [wasCorrect, setWasCorrect] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 12;
    ctx.strokeStyle = "#0EA5E9";
  }, [word.word, phase]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setTracing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!tracing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stop = () => {
    if (!tracing) return;
    setTracing(false);
    setStrokeCount((s) => s + 1);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setStrokeCount(0);
  };

  if (phase === "trace") {
    return (
      <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-5 sm:p-8">
        <p className="text-center font-nunito text-sm text-slate-600 uppercase tracking-widest mb-3">
          Trace the word with your finger
        </p>
        <div className="text-center mb-2">
          <button
            onClick={() => speak(word.word)}
            className="inline-flex items-center gap-2 text-primary font-fredoka font-bold text-sm"
            data-testid="vocab-trace-hear-btn"
          >
            <SpeakerHigh size={18} weight="fill" />
            Hear "{word.word}"
          </button>
        </div>
        <div className="relative bg-yellow-50 border-4 border-dashed border-foreground/50 rounded-2xl h-52 sm:h-64">
          <span
            className="absolute inset-0 flex items-center justify-center font-fredoka font-bold text-6xl sm:text-7xl text-foreground/20 pointer-events-none select-none"
            aria-hidden
          >
            {word.word}
          </span>
          <canvas
            ref={canvasRef}
            onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
            onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
            className="absolute inset-0 w-full h-full rounded-2xl touch-none"
            data-testid="vocab-trace-canvas"
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={clear} variant="outline" className="font-nunito border-2 border-foreground rounded-xl">
            <ArrowClockwise size={16} weight="bold" className="mr-1.5" />
            Clear
          </Button>
          <Button
            onClick={() => { if (strokeCount === 0) return toast.info("Trace first!"); setPhase("say"); }}
            disabled={strokeCount === 0}
            className="flex-1 bg-primary text-white font-fredoka font-bold border-2 border-foreground shadow-brutal-sm rounded-xl py-5"
            data-testid="vocab-trace-done-btn"
          >
            I traced it!
            <ArrowRight size={20} weight="bold" className="ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "say") {
    return (
      <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-5 sm:p-8">
        <p className="text-center font-nunito text-sm text-slate-600 uppercase tracking-widest mb-2">
          Now say it in a sentence
        </p>
        <p className="text-center font-fredoka font-bold text-3xl text-foreground mb-1">"{word.word}"</p>
        <p className="text-center font-nunito text-slate-600 text-sm mb-4">
          <em>{word.definition}</em>
        </p>
        <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4 mb-4">
          <p className="font-nunito text-foreground text-sm">
            <strong>Example:</strong> {word.sentence_template.sentence}
          </p>
          <button
            onClick={() => speak(word.sentence_template.sentence)}
            className="mt-2 inline-flex items-center gap-1 text-primary font-fredoka font-bold text-sm"
          >
            <SpeakerHigh size={16} weight="fill" />
            Hear the example
          </button>
        </div>
        <p className="font-nunito text-sm text-slate-600 mb-3">Did you say a sentence with the word?</p>
        <div className="flex gap-2">
          <Button
            onClick={() => { setWasCorrect(false); setPhase("feedback"); }}
            variant="outline"
            className="flex-1 font-nunito border-2 border-foreground rounded-xl"
            data-testid="vocab-trace-nope-btn"
          >
            <X size={16} weight="bold" className="mr-1.5" />
            Not yet
          </Button>
          <Button
            onClick={() => { setWasCorrect(true); setPhase("feedback"); }}
            className="flex-1 bg-primary text-white font-fredoka font-bold border-2 border-foreground shadow-brutal-sm rounded-xl"
            data-testid="vocab-trace-yes-btn"
          >
            <Check size={18} weight="bold" className="mr-1.5" />
            Yes I did!
          </Button>
        </div>
      </div>
    );
  }

  // feedback
  return (
    <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-5 sm:p-8">
      <p className="text-center font-fredoka font-bold text-2xl">"{word.word}"</p>
      <Feedback
        correct={wasCorrect}
        word={word.word}
        definition={word.definition}
        onRetry={() => { clear(); setPhase("trace"); }}
        onNext={() => onDone(wasCorrect)}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Progress view — private, per-child mastery
// ─────────────────────────────────────────────────────────────
const ProgressView = ({ profile, onBack }) => {
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    axios.get(`${API}/vocab/progress/${profile.id}`)
      .then((r) => setProgress(r.data))
      .catch(() => {});
  }, [profile.id]);

  if (!progress) return <p className="text-center font-nunito py-16">Loading progress…</p>;

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack} className="font-nunito font-bold text-sm text-foreground/70 mb-4">
        ← Back
      </button>

      <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 mb-4">
        <h2 className="font-fredoka font-bold text-2xl">{profile.child_nickname}'s progress</h2>
        <p className="font-nunito text-xs text-slate-500 mb-4">Private — only you can see this.</p>
        <div className="grid grid-cols-3 gap-3">
          <Stat num={progress.total_attempts} label="Rounds played" />
          <Stat num={`${progress.accuracy}%`} label="Right answers" color="text-emerald-600" />
          <Stat num={progress.words_seen} label="Words seen" color="text-primary" />
        </div>
      </div>

      {progress.needs_practice.length > 0 && (
        <div className="bg-amber-50 border-4 border-foreground rounded-3xl shadow-brutal p-6 mb-4" data-testid="vocab-needs-practice">
          <h3 className="font-fredoka font-bold text-lg mb-2">Words to practice</h3>
          <div className="flex flex-wrap gap-2">
            {progress.needs_practice.map((w) => (
              <span key={w.word} className="bg-white border-2 border-foreground rounded-full px-3 py-1 font-nunito font-bold text-sm">
                {w.word} <span className="text-slate-500">· {w.accuracy}%</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {progress.mastered.length > 0 && (
        <div className="bg-emerald-50 border-4 border-foreground rounded-3xl shadow-brutal p-6" data-testid="vocab-mastered">
          <h3 className="font-fredoka font-bold text-lg mb-2">Mastered ⭐</h3>
          <div className="flex flex-wrap gap-2">
            {progress.mastered.map((w) => (
              <span key={w.word} className="bg-white border-2 border-foreground rounded-full px-3 py-1 font-nunito font-bold text-sm">
                {w.word} <span className="text-emerald-600">· {w.accuracy}%</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ num, label, color = "text-foreground" }) => (
  <div className="text-center">
    <p className={`font-fredoka font-bold text-3xl leading-none ${color}`}>{num}</p>
    <p className="font-nunito text-xs text-slate-600 uppercase tracking-wide mt-1">{label}</p>
  </div>
);

export default KidsVocab;
